import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  useMatchChain,
  useUserInfo as useMatchIdUserInfo,
  useWallet as useMatchIdWallet,
} from '@matchain/matchid-sdk-react/hooks';
import { LoginMethodType } from '@matchain/matchid-sdk-react/types';
import { WalletClient } from 'viem';

import matchIdIcon from 'modules/common/icons/match-id-white-icon.png';
import { uid } from 'modules/common/utils/uid';

import { DEFAULT_CONNECTION_KEY, EConnectionType } from '../const';
import {
  resetConnectionState,
  setConnectionType,
} from '../store/connectionSlice';
import { IUseRawConnectionData } from '../types';
import { getCreateWalletClientParams } from '../utils/getCreateWalletClientParams';

interface IUseMatchIdConnectionData
  extends Omit<IUseRawConnectionData, 'connect'> {
  connect: (method: LoginMethodType) => Promise<void>;
}

export const SELECTED_CONNECTION_TYPE = EConnectionType.MatchId;

export function useMatchIdConnection(): IUseMatchIdConnectionData {
  const dispatch = useDispatch();

  const {
    isLogin,
    login: connectMatchId,
    logout: disconnect,
  } = useMatchIdUserInfo();
  const walletIdData = useMatchIdWallet();
  const {
    walletReady,
    createWalletClient,
    address: matchIdAccountAddress,
  } = walletIdData;

  const { chainId } = useMatchChain();
  const createWalletClientRef = useRef(createWalletClient);
  createWalletClientRef.current = createWalletClient;

  const isConnected =
    walletReady &&
    isLogin &&
    !!createWalletClientRef?.current &&
    !!matchIdAccountAddress;

  const getWalletKey = useCallback(() => {
    if (!isConnected) {
      return DEFAULT_CONNECTION_KEY;
    }

    const uidKey =
      (
        createWalletClientRef?.current(
          getCreateWalletClientParams(),
        ) as WalletClient
      )?.uid || uid();

    return `match-id-${uidKey}`;
  }, [isConnected]);

  const connect: IUseMatchIdConnectionData['connect'] = useCallback(
    async method => {
      if (!method) {
        return;
      }

      await connectMatchId(method);

      dispatch(setConnectionType(SELECTED_CONNECTION_TYPE));
    },
    [connectMatchId, dispatch],
  );

  const disconnectRef = useRef(disconnect);
  disconnectRef.current = disconnect;
  const handleDisconnect = useCallback(async () => {
    await disconnectRef.current();

    dispatch(resetConnectionState(SELECTED_CONNECTION_TYPE));
  }, [dispatch]);

  return {
    connect,
    disconnect: handleDisconnect,
    isConnected,
    getWalletKey,
    chainId: chainId ?? null,
    walletIcon: matchIdIcon,
    accountAddress: !matchIdAccountAddress ? null : matchIdAccountAddress,
  };
}
